import { useState } from 'react'
import { FiChevronDown } from 'react-icons/fi'
import SeoHead from '@presentation/seo/SeoHead'

type FaqItem = {
  id: string
  question: string
  answerParagraphs: string[]
  answerSteps?: string[]
}

const FAQ_ITEMS: FaqItem[] = [
  {
    id: 'delete-account',
    question: 'How to delete the account?',
    answerParagraphs: [
      'You can delete your account from your profile settings with a protected multi-step confirmation flow.',
      'For your safety, deletion is intentionally separated from logout and requires both the confirmation word and your current password.',
    ],
    answerSteps: [
      'Open your profile modal from your account avatar.',
      'Tap the Settings cog wheel in the top-left corner.',
      'On the Settings screen, tap Delete Account at the bottom.',
      'In the confirmation modal, type delete (or DELETE) in the confirmation field.',
      'Enter your current password to re-authenticate your account.',
      'Tap Permanently Delete when the button becomes active.',
    ],
  },
  {
    id: 'shared-account',
    question: "Is my account working in both PD Buddy app and Parkinson's Insights?",
    answerParagraphs: [
      "Yes. The same account works across both PD Buddy and Parkinson's Insights.",
      'You can use one login across both platforms, so your account access remains consistent.',
    ],
  },
  {
    id: 'update-profile',
    question: 'How do I update my name or profile photo?',
    answerParagraphs: [
      'Open your profile modal, update your full name, and tap Save to keep your changes.',
      'To change your profile photo, tap your avatar and choose or capture a new image.',
    ],
  },
  {
    id: 'forgot-password',
    question: 'I forgot my password. What should I do?',
    answerParagraphs: [
      'Open the login modal and use the password reset option.',
      'Follow the reset email instructions, then sign in again with your new password.',
    ],
  },
  {
    id: 'public-content',
    question: 'Do I need to be logged in to read content?',
    answerParagraphs: [
      'Most public content, including news, webinars, podcasts, and hacks, can be browsed without logging in.',
      'Authentication is required for account-specific actions such as profile updates, favorites, notifications, and similar personalized features.',
    ],
  },
]

export default function FaqsPage() {
  const [expandedFaqId, setExpandedFaqId] = useState<string | null>(null)

  return (
    <main className="bg-background py-8 sm:py-12">
      <SeoHead
        title="FAQs | Parkinson's Insights"
        description="Find answers to frequently asked questions about accounts, profile settings, and how to use Parkinson's Insights and PD Buddy."
        canonicalPath="/legal/faqs"
      />
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-border-light bg-card-surface p-6 shadow-sm sm:p-8">
          <header className="border-b border-border-light pb-5 sm:pb-6">
            <h1 className="text-2xl font-bold tracking-tight text-blue sm:text-3xl">
              Frequently Asked Questions
            </h1>
            <p className="mt-2 text-sm font-medium text-text-secondary">
              Helpful answers about account access, profile settings, and platform usage.
            </p>
          </header>

          <div>
            {FAQ_ITEMS.map((item) => {
              const panelId = `faq-panel-${item.id}`
              const buttonId = `faq-button-${item.id}`
              const isExpanded = expandedFaqId === item.id

              return (
                <section
                  key={item.id}
                  className="border-b border-border-light py-2 last:border-b-0 sm:py-3"
                >
                  <button
                    id={buttonId}
                    type="button"
                    aria-expanded={isExpanded}
                    aria-controls={panelId}
                    onClick={() => {
                      setExpandedFaqId((currentId) => (currentId === item.id ? null : item.id))
                    }}
                    className="flex w-full items-center justify-between gap-4 rounded-xl px-3 py-3 text-left transition-colors hover:bg-border-light focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue"
                  >
                    <span className="text-base font-semibold text-blue sm:text-lg">{item.question}</span>
                    <FiChevronDown
                      aria-hidden="true"
                      className={`h-5 w-5 shrink-0 text-blue transition-transform ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {isExpanded ? (
                    <div
                      id={panelId}
                      role="region"
                      aria-labelledby={buttonId}
                      className="space-y-3 px-3 pb-3 pt-1"
                    >
                      {item.answerParagraphs.map((paragraph) => (
                        <p key={paragraph} className="text-base leading-relaxed text-text-primary">
                          {paragraph}
                        </p>
                      ))}
                      {item.answerSteps ? (
                        <ol className="ml-5 list-decimal space-y-2 text-base leading-relaxed text-text-primary">
                          {item.answerSteps.map((step) => (
                            <li key={step}>{step}</li>
                          ))}
                        </ol>
                      ) : null}
                    </div>
                  ) : null}
                </section>
              )
            })}
          </div>
        </div>
      </div>
    </main>
  )
}
